// FIX-OPS-UPLOAD-TYPES (owner, 2026-10-15, "option 1"): iPhone HEIC/HEIF
// photos become JPEG before upload, with the browser's OWN decoder and no
// library, so every receipt previews like any photo.
//
// Where the platform decodes HEIC (WebKit: every browser on iPhone/iPad, and
// Safari on the Mac), the image is drawn on a canvas and saved as JPEG at
// quality 0.85. Orientation: createImageBitmap is asked for
// imageOrientation 'from-image', and an <img> draws upright on a canvas, so
// the JPEG is stored the way the photo looks. Everywhere else (desktop
// Chrome/Edge/Firefox) decoding fails and the ORIGINAL file is uploaded
// unchanged, as before (OPS accepts HEIC; the screens show a file tile).
// Feature-detected, never by user agent. A conversion problem never loses
// the receipt: every failure returns the original file.

export const HEIC_JPEG_QUALITY = 0.85;
// The whole conversion (header, decode, encode) gets this long, once.
export const HEIC_CONVERT_TIMEOUT_MS = 15_000;
// Never decode a file larger than OPS would store anyway.
export const HEIC_MAX_INPUT_BYTES = 10 * 1024 * 1024;
// Bigger photos are uploaded as the original HEIC. The largest real iPhone
// photos tested on iOS Safari are 24.5 MP (36 of 36 converted); a 48 MP
// "HEIF Max" photo (~195 MB decoded) is not decoded on the phone.
export const HEIC_MAX_SOURCE_PIXELS = 25_000_000;
// iOS Safari's largest canvas is 16,777,216 pixels: bigger photos are
// scaled down to fit, keeping their shape.
export const HEIC_MAX_CANVAS_PIXELS = 16_000_000;

const TIMEOUT_MESSAGE = 'HEIC conversion timed out';

const HEIC_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

const HEIC_NAME = /\.(heic|heif)$/i;

/** HEIC/HEIF by MIME type or, when the browser gives none, by name. */
export const isHeicFile = (file: File) =>
  HEIC_TYPES.has((file.type || '').toLowerCase()) ||
  (!file.type && HEIC_NAME.test(file.name));

const readHead = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });

// DEV-FUNCTIONAL-PKG2 D6: Windows Chrome gives a .heic/.heif file no MIME
// type, so a picker that checks the type alone refused it. The receipt
// pickers now accept .heic/.heif by extension (config
// acceptPdfAndImageFileTypes; react-dropzone's file-selector also names an
// untyped file from its extension alone), and DropZone then checks the
// CONTENT before anything else sees the file: a HEIF file starts with an
// 'ftyp' box whose major or compatible brands name HEIC/HEIF (ISO/IEC
// 23008-12). Anything else named .heic/.heif is refused, never uploaded.
// OPS sniffs every upload again on the server (unchanged).
export const HEIF_BRAND_TYPES: Readonly<Record<string, string>> = Object.freeze(
  {
    heic: 'image/heic',
    heix: 'image/heic',
    hevc: 'image/heic',
    mif1: 'image/heif',
    msf1: 'image/heif',
  }
);
// The ftyp box of a real photo is a few dozen bytes; never read more.
const FTYP_SCAN_BYTES = 256;

const ascii = (bytes: Uint8Array, at: number) =>
  String.fromCharCode(bytes[at], bytes[at + 1], bytes[at + 2], bytes[at + 3]);

/** The brands of the 'ftyp' box a file starts with (major first), or []. */
export const ftypBrands = async (file: Blob): Promise<string[]> => {
  const bytes = new Uint8Array(await readHead(file.slice(0, FTYP_SCAN_BYTES)));
  if (bytes.length < 16 || ascii(bytes, 4) !== 'ftyp') {
    return [];
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const size = view.getUint32(0);
  if (size < 16) {
    return [];
  }
  // major brand, minor version, then compatible brands up to the box end.
  const brands = [ascii(bytes, 8)];
  for (let at = 16; at + 4 <= Math.min(size, bytes.length); at += 4) {
    brands.push(ascii(bytes, at));
  }
  return brands;
};

// AVIF is also built on HEIF and lists mif1 among its brands: an AVIF file
// (or one named .heic) is not a HEIC/HEIF photo.
const AVIF_BRANDS = new Set(['avif', 'avis']);

/** 'image/heic' or 'image/heif' when the content is HEIC/HEIF, else null. */
export const sniffHeifType = async (file: Blob): Promise<string | null> => {
  const brands = await ftypBrands(file);
  const types = brands
    .map((brand) => HEIF_BRAND_TYPES[brand])
    .filter((type): type is string => Boolean(type));
  if (types.includes('image/heic')) {
    return 'image/heic';
  }
  if (!types.length || brands.some((brand) => AVIF_BRANDS.has(brand))) {
    return null;
  }
  return 'image/heif';
};

/**
 * The file a picker may take for a name ending .heic/.heif (D6):
 * - another image type the browser named (e.g. image/jpeg): unchanged;
 * - an empty, generic or HEIC/HEIF type: its content must be HEIC/HEIF
 *   (sniffHeifType); it is returned as a File typed HEIC/HEIF (its own type,
 *   else the sniffed one), so it takes the same path as a Mac/iPhone photo
 *   (preview, the native JPEG conversion, OPS);
 * - content that is not HEIC/HEIF: null (refused).
 * Any other name: unchanged.
 */
export const heicUploadFile = async (file: File): Promise<File | null> => {
  if (!HEIC_NAME.test(file.name || '')) {
    return file;
  }
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/') && !HEIC_TYPES.has(type)) {
    return file;
  }
  let sniffed: string | null = null;
  try {
    sniffed = await sniffHeifType(file);
  } catch {
    sniffed = null;
  }
  if (!sniffed) {
    return null;
  }
  // Always a new File carrying the type in its own bytes' record: the
  // picker's file-selector names an untyped file only on the JS object, so
  // the preview (FileReader) and the multipart upload would still say
  // application/octet-stream. The bytes are shared, not copied.
  return new File([file], file.name, {
    lastModified: file.lastModified,
    type: HEIC_TYPES.has(type) ? type : sniffed,
  });
};

type Drawable = {
  width: number;
  height: number;
  release: () => void;
  source: CanvasImageSource;
};

const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  onLate?: (value: T) => void
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      done = true;
      reject(new Error(TIMEOUT_MESSAGE));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        if (done) {
          // Arrived after the timeout: free it.
          onLate?.(value);
          return;
        }
        done = true;
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        if (!done) {
          done = true;
          reject(error);
        }
      }
    );
  });

const decodeWithBitmap = async (file: File): Promise<Drawable> => {
  const bitmap = await createImageBitmap(file, {
    imageOrientation: 'from-image',
  });
  return {
    source: bitmap,
    width: bitmap.width,
    height: bitmap.height,
    release: () => bitmap.close(),
  };
};

const decodeWithImage = (file: File, timeoutMs: number): Promise<Drawable> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const stop = () => {
      image.onload = null;
      image.onerror = null;
      image.removeAttribute('src');
      URL.revokeObjectURL(url);
    };
    // A load that never ends: stop the decode and free the URL.
    const timer = setTimeout(() => {
      stop();
      reject(new Error(TIMEOUT_MESSAGE));
    }, timeoutMs);
    image.onload = () => {
      clearTimeout(timer);
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        release: () => URL.revokeObjectURL(url),
      });
    };
    image.onerror = () => {
      clearTimeout(timer);
      stop();
      reject(new Error('HEIC is not decodable here'));
    };
    image.src = url;
  });

const remaining = (deadline: number) => Math.max(0, deadline - Date.now());

const decode = async (file: File, deadline: number): Promise<Drawable> => {
  const release = (drawable: Drawable) => drawable.release();
  if (typeof createImageBitmap === 'function') {
    try {
      return await withTimeout(
        decodeWithBitmap(file),
        remaining(deadline),
        release
      );
    } catch (error) {
      if ((error as Error)?.message === TIMEOUT_MESSAGE) {
        // Still decoding: never start a second decode of the same photo.
        throw error;
      }
      // Some WebKit builds decode HEIC in <img> but not in
      // createImageBitmap: try the element before giving up.
    }
  }
  return decodeWithImage(file, remaining(deadline));
};

// HEIF keeps each image's size in an 'ispe' box near the start of the file
// (size, 'ispe', version/flags, width, height). Reading them first means a
// huge photo is never handed to the decoder at all.
const ISPE = [0x69, 0x73, 0x70, 0x65];
const HEADER_SCAN_BYTES = 512 * 1024;

/** Largest declared image size (width × height) in the HEIF header, or 0. */
export const declaredHeifPixels = async (file: File) => {
  const bytes = new Uint8Array(
    await readHead(file.slice(0, HEADER_SCAN_BYTES))
  );
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let largest = 0;
  for (let at = 4; at + 16 <= bytes.length; at += 1) {
    if (
      bytes[at] === ISPE[0] &&
      bytes[at + 1] === ISPE[1] &&
      bytes[at + 2] === ISPE[2] &&
      bytes[at + 3] === ISPE[3]
    ) {
      const pixels = view.getUint32(at + 8) * view.getUint32(at + 12);
      largest = Math.max(largest, pixels);
    }
  }
  return largest;
};

/** Output size: the source, scaled down to fit the canvas limit. */
export const jpegSize = (width: number, height: number) => {
  const scale = Math.min(
    1,
    Math.sqrt(HEIC_MAX_CANVAS_PIXELS / Math.max(1, width * height))
  );
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
  };
};

const jpegName = (name: string) =>
  `${name.replace(/\.(heic|heif)$/i, '') || 'photo'}.jpg`;

/**
 * The file to upload: a JPEG copy of a HEIC/HEIF photo when this browser
 * can decode it, otherwise the original file, unchanged.
 */
export const convertHeicToJpeg = async (
  file: File,
  { timeoutMs = HEIC_CONVERT_TIMEOUT_MS }: { timeoutMs?: number } = {}
): Promise<File> => {
  if (!isHeicFile(file) || file.size > HEIC_MAX_INPUT_BYTES) {
    return file;
  }
  let drawable: Drawable | null = null;
  let canvas: HTMLCanvasElement | null = null;
  const deadline = Date.now() + timeoutMs;
  try {
    if (
      (await withTimeout(declaredHeifPixels(file), remaining(deadline))) >
      HEIC_MAX_SOURCE_PIXELS
    ) {
      return file;
    }
    drawable = await decode(file, deadline);
    const pixels = drawable.width * drawable.height;
    if (!pixels || pixels > HEIC_MAX_SOURCE_PIXELS) {
      return file;
    }
    const { width, height } = jpegSize(drawable.width, drawable.height);
    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }
    // White under any transparent area (JPEG has no alpha).
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(drawable.source, 0, 0, width, height);
    const target = canvas;
    const blob = await withTimeout(
      new Promise<Blob | null>((resolve) =>
        target.toBlob(resolve, 'image/jpeg', HEIC_JPEG_QUALITY)
      ),
      remaining(deadline)
    );
    // A JPEG larger than OPS stores (10 MB) would lose the receipt: keep the
    // HEIC, which OPS accepts.
    if (
      !blob ||
      !blob.size ||
      blob.size > HEIC_MAX_INPUT_BYTES ||
      blob.type !== 'image/jpeg'
    ) {
      return file;
    }
    return new File([blob], jpegName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    // Not decodable here (desktop Chrome/Edge/Firefox), too slow, or
    // broken: upload the photo exactly as picked.
    return file;
  } finally {
    drawable?.release();
    if (canvas) {
      // Frees the backing store at once (iOS keeps canvas memory otherwise).
      canvas.width = 0;
      canvas.height = 0;
    }
  }
};
